import {
  UNIZO_FEATURE_FLAGS,
  type UnizoFeatureFlagKey,
} from "@shared-domain/unizo/featureFlags";
import {
  DEFAULT_CUSTOMER_NUMBER_LABEL,
  isCustomerNumberLabelEnabled,
  resolveCustomerNumberLabel,
} from "@shared-domain/unizo/customerNumberLabel";
import {
  HomeScreenSchema,
  type HomeScreenPreference,
} from "@shared-domain/unizo/contract";

/**
 * テナント別フィーチャーフラグの判定（純関数）
 *
 * PRODUCT_DEV-782 で `useTenantFeatureFlag` から出した。**ローダの中でも同じ判定が要る**
 * ためで、フックのままだと React の外から呼べない。判定順は従来と同じ
 * （テナント設定 → レジストリの既定値 → false）。
 */
export interface TenantFlagAuth {
  info?: { productId?: string };
  currentTenantId?: string;
  memberships?: readonly {
    tenantId: string;
    products?: readonly {
      id: string;
      featureFlags?: Partial<Record<string, boolean>>;
      /** PRODUCT_DEV-531: テナントが Sansan 連携を使うか（whoami が判定済みの値。無ければ使わない） */
      sansanEnabled?: boolean;
    }[];
    /** PRODUCT_DEV-1070: 会社の初期表示ページ（whoami の生値。読むのは `readTenantHomeScreenDefault` だけ） */
    homeScreenDefault?: unknown;
    /** PRODUCT_DEV-1239: 顧客番号の呼び名（whoami の生値。読むのは `resolveTenantCustomerNumber` だけ） */
    customerNumberLabel?: unknown;
  }[];
}

/** 今のテナントの所属（whoami の memberships から）。 */
export const findCurrentMembership = (auth: TenantFlagAuth | null | undefined) =>
  auth?.memberships?.find((m) => m.tenantId === auth?.currentTenantId);

/**
 * PRODUCT_DEV-1070: 会社の初期表示ページ（テナント設定。whoami の今の所属に載る）。未設定・読めなければ null。
 * アプリを開いた瞬間（root-loader）でも読めるよう、設定 API ではなく whoami から取る。
 * 個人設定との合わせ方は `lib/home` の `resolveHome`。
 */
export const readTenantHomeScreenDefault = (
  auth: TenantFlagAuth | null | undefined,
): HomeScreenPreference | null => {
  const parsed = HomeScreenSchema.safeParse(findCurrentMembership(auth)?.homeScreenDefault);
  return parsed.success ? parsed.data : null;
};

/**
 * PRODUCT_DEV-1239: 顧客番号の呼び名と番号検索が使えるか（2つのフラグが両方 ON）と、画面に出す名前。
 * 使えない・未設定なら `fallback`（画面ごとの既定名）。判定の正本は shared の `customerNumberLabel.ts`。
 */
export const resolveTenantCustomerNumber = (
  auth: TenantFlagAuth | null | undefined,
  fallback: string = DEFAULT_CUSTOMER_NUMBER_LABEL,
): { enabled: boolean; label: string } => {
  const enabled = isCustomerNumberLabelEnabled((key) =>
    resolveTenantFeatureFlag(auth, key as UnizoFeatureFlagKey),
  );
  const raw = findCurrentMembership(auth)?.customerNumberLabel;
  return {
    enabled,
    label: resolveCustomerNumberLabel(enabled, typeof raw === "string" ? raw : null, fallback),
  };
};

/** 今のテナント×プロダクト（whoami の今の所属の products から）。 */
const findCurrentProduct = (auth: TenantFlagAuth | null | undefined) => {
  const productId = auth?.info?.productId;
  return findCurrentMembership(auth)?.products?.find((p) => p.id === productId);
};

/**
 * PRODUCT_DEV-531: テナントがテナント設定で Sansan 連携を「使う」にしているか。
 * 機能フラグ `sansanContactRegistration`（運営が開ける）とは別の値で、ボタンは両方 ON のときだけ出す。
 */
export const resolveTenantSansanEnabled = (
  auth: TenantFlagAuth | null | undefined,
): boolean => findCurrentProduct(auth)?.sansanEnabled === true;

export const resolveTenantFeatureFlag = (
  auth: TenantFlagAuth | null | undefined,
  key: UnizoFeatureFlagKey,
): boolean => {
  const currentProduct = findCurrentProduct(auth);
  return (
    currentProduct?.featureFlags?.[key] ??
    UNIZO_FEATURE_FLAGS[key].defaultValue ??
    false
  );
};

/**
 * PRODUCT_DEV-908: 名前を文字列で持っているフラグ（関連リンクの常設ページの featureFlag など）の判定。
 * レジストリに無い名前は OFF（知らないフラグで開けない画面を押せるようにしない）。
 */
export const resolveTenantFeatureFlagByName = (
  auth: TenantFlagAuth | null | undefined,
  name: string,
): boolean =>
  Object.prototype.hasOwnProperty.call(UNIZO_FEATURE_FLAGS, name) &&
  resolveTenantFeatureFlag(auth, name as UnizoFeatureFlagKey);
